import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Info, ShieldCheck } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface EditMemberDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: any;
  onSuccess: () => void;
}

const ROLE_DESCRIPTIONS = {
  chair: "Leads the PTA, chairs meetings, and oversees all operations",
  treasurer: "Manages finances, budgets, and financial reporting",
  trustee: "Provides governance oversight and strategic guidance",
  secretary: "Records minutes, manages communications, and handles admin",
  catering: "Organizes refreshments and catering for PTA events",
  communications: "Manages newsletters, social media, and public relations",
  community: "Builds relationships with parents and local community",
  assets: "Manages PTA equipment, inventory, and physical resources",
  events: "Plans and coordinates PTA fundraising events",
  school_liaison: "Coordinates with school staff and manages school-PTA relations",
  shop: "Manages the PTA shop operations and inventory",
  occasional_helper: "Volunteers for specific events when available",
  custom: "Custom role with specific responsibilities defined by PTA",
};

const DBS_TYPES = [
  { value: "basic", label: "Basic" },
  { value: "standard", label: "Standard" },
  { value: "enhanced", label: "Enhanced" },
  { value: "enhanced_barred", label: "Enhanced with Barred List" },
];

export function EditMemberDialog({ open, onOpenChange, member, onSuccess }: EditMemberDialogProps) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [mainRoles, setMainRoles] = useState({
    chair: false,
    treasurer: false,
    trustee: false,
    secretary: false,
  });
  const [additionalRoles, setAdditionalRoles] = useState({
    catering: false,
    communications: false,
    community: false,
    assets: false,
    events: false,
    school_liaison: false,
    shop: false,
    occasional_helper: false,
    custom: false,
  });
  const [additionalAccess, setAdditionalAccess] = useState({
    documents: false,
    expenses: false,
  });
  const [documentPermission, setDocumentPermission] = useState<"read" | "edit">("read");
  const [forumAccessDisabled, setForumAccessDisabled] = useState(false);
  const [memberSince, setMemberSince] = useState("");
  const [loading, setLoading] = useState(false);
  
  // DBS Check fields
  const [dbsChecked, setDbsChecked] = useState(false);
  const [dbsCertificateNumber, setDbsCertificateNumber] = useState("");
  const [dbsIssueDate, setDbsIssueDate] = useState("");
  const [dbsExpiryDate, setDbsExpiryDate] = useState("");
  const [dbsType, setDbsType] = useState("enhanced");
  const [dbsNotes, setDbsNotes] = useState("");

  useEffect(() => {
    if (member && open) {
      loadMemberData();
    }
  }, [member, open]);

  const loadMemberData = async () => {
    console.log('=== LOADING MEMBER DATA ===');
    console.log('Member object:', member);
    console.log('Member ID (member table):', member.id);
    console.log('User ID (auth user):', member.user_id);
    
    // Set name and email from member
    setFullName(member.full_name || "");
    setEmail(member.email || "");
    
    // Only load roles if member has a user_id (is a registered user)
    if (member.user_id) {
      console.log('Fetching roles for user_id:', member.user_id);
      // Load roles - use member.user_id (auth user id), not member.id (member table id)
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', member.user_id);

      console.log('Roles query result:', { roles, error });

      if (roles) {
        // Filter out site_admin - PTA admins should never see or manage this role
        const roleNames = roles.map(r => r.role).filter(r => r !== 'site_admin');
        console.log('Role names found:', roleNames);
        setMainRoles({
          chair: roleNames.includes('chair'),
          treasurer: roleNames.includes('treasurer'),
          trustee: roleNames.includes('trustee'),
          secretary: roleNames.includes('secretary'),
        });
        setAdditionalRoles({
          catering: roleNames.includes('catering'),
          communications: roleNames.includes('communications'),
          community: roleNames.includes('community'),
          assets: roleNames.includes('assets'),
          events: roleNames.includes('events'),
          school_liaison: roleNames.includes('school_liaison'),
          shop: roleNames.includes('shop'),
          occasional_helper: roleNames.includes('occasional_helper'),
          custom: roleNames.includes('custom'),
        });
      }
    } else {
      console.log('No user_id - member not registered, resetting roles');
      // Reset roles for non-registered members
      setMainRoles({
        chair: false,
        treasurer: false,
        trustee: false,
        secretary: false,
      });
      setAdditionalRoles({
        catering: false,
        communications: false,
        community: false,
        assets: false,
        events: false,
        school_liaison: false,
        shop: false,
        occasional_helper: false,
        custom: false,
      });
    }

    // Load additional access
    const { data: access } = await supabase
      .from('member_additional_access')
      .select('*')
      .eq('member_id', member.id);

    if (access) {
      setAdditionalAccess({
        documents: access.some(a => a.access_type === 'documents'),
        expenses: access.some(a => a.access_type === 'expenses'),
      });
      const docAccess = access.find(a => a.access_type === 'documents');
      if (docAccess?.permission_level) {
        setDocumentPermission(docAccess.permission_level as "read" | "edit");
      }
    }

    // Load forum access status
    setForumAccessDisabled(member.forum_access_disabled || false);
    
    // Load member since date
    setMemberSince(member.member_since || "");
    
    // Load DBS check data
    console.log('=== DBS DATA FROM MEMBER OBJECT ===');
    console.log('dbs_checked:', member.dbs_checked);
    console.log('dbs_certificate_number:', member.dbs_certificate_number);
    console.log('dbs_issue_date:', member.dbs_issue_date);
    console.log('dbs_expiry_date:', member.dbs_expiry_date);
    console.log('dbs_type:', member.dbs_type);
    console.log('dbs_notes:', member.dbs_notes);
    
    setDbsChecked(member.dbs_checked === true);
    setDbsCertificateNumber(member.dbs_certificate_number || "");
    setDbsIssueDate(member.dbs_issue_date || "");
    setDbsExpiryDate(member.dbs_expiry_date || "");
    setDbsType(member.dbs_type || "enhanced");
    setDbsNotes(member.dbs_notes || "");
  };

  const handleSubmit = async () => {
    setLoading(true);

    console.log('=== SAVING MEMBER DATA ===');
    console.log('Member ID:', member.id);
    console.log('User ID:', member.user_id);
    console.log('Main roles state:', mainRoles);
    console.log('Additional roles state:', additionalRoles);

    // Validate name and email
    if (!fullName.trim()) {
      toast({
        title: "Validation Error",
        description: "Name is required",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    if (!email.trim() || !email.includes('@')) {
      toast({
        title: "Validation Error",
        description: "Valid email is required",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Validate roles
    if (mainRoles.chair && mainRoles.treasurer) {
      toast({
        title: "Invalid Selection",
        description: "A member cannot be both Chair and Treasurer",
        variant: "destructive",
      });
      setLoading(false);
      return;
    }

    // Only update user_roles if member has a user_id (is a registered user)
    if (member.user_id) {
      console.log('Deleting existing roles for user_id:', member.user_id);
      // Delete existing roles EXCEPT site_admin - PTA admins cannot modify site_admin role
      const { error: deleteError } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', member.user_id)
        .neq('role', 'site_admin');
      console.log('Delete result:', { deleteError });

      // Add new roles (filter out site_admin as extra protection)
      const rolesToAdd = [
        ...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k),
        ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k),
      ].filter(role => role !== 'site_admin');

      console.log('Roles to add:', rolesToAdd);

      if (rolesToAdd.length > 0) {
        const roleInserts = rolesToAdd.map(role => ({
          user_id: member.user_id,
          role: role as any,
        }));
        console.log('Inserting roles:', roleInserts);
        
        const { data: insertedRoles, error: insertError } = await supabase
          .from('user_roles')
          .insert(roleInserts)
          .select();
        
        console.log('Insert result:', { insertedRoles, insertError });
        
        if (insertError) {
          toast({
            title: "Error saving roles",
            description: insertError.message,
            variant: "destructive",
          });
          setLoading(false);
          return;
        }
      }
    }

    // Always update member role field for display purposes (even for non-registered members)
    const allRoles = [
      ...Object.entries(mainRoles).filter(([_, v]) => v).map(([k]) => k),
      ...Object.entries(additionalRoles).filter(([_, v]) => v).map(([k]) => k),
    ];

    console.log('Updating member record with roles:', allRoles.join(', '));

    // Update member role field, name, email, forum access, member_since, and DBS data
    await supabase
      .from('members')
      .update({
        full_name: fullName.trim(),
        email: email.trim(),
        role: allRoles.join(', '),
        forum_access_disabled: forumAccessDisabled,
        member_since: memberSince || null,
        dbs_checked: dbsChecked,
        dbs_certificate_number: dbsChecked ? dbsCertificateNumber || null : null,
        dbs_issue_date: dbsChecked && dbsIssueDate ? dbsIssueDate : null,
        dbs_expiry_date: dbsChecked && dbsExpiryDate ? dbsExpiryDate : null,
        dbs_type: dbsChecked ? dbsType : null,
        dbs_notes: dbsChecked ? dbsNotes || null : null,
      })
      .eq('id', member.id);

    // Delete existing access
    await supabase.from('member_additional_access').delete().eq('member_id', member.id);

    // Add new access
    const accessToAdd = Object.entries(additionalAccess)
      .filter(([_, v]) => v)
      .map(([type]) => ({
        member_id: member.id,
        access_type: type,
        permission_level: type === 'documents' ? documentPermission : null,
      }));

    if (accessToAdd.length > 0) {
      await supabase.from('member_additional_access').insert(accessToAdd);
    }

    setLoading(false);
    toast({
      title: "Success",
      description: "Member updated successfully"
    });
    onSuccess();
    onOpenChange(false);
  };

  const hasOccasionalHelper = additionalRoles.occasional_helper;
  const hasMainRole = Object.values(mainRoles).some((v) => v);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Member</DialogTitle>
          <DialogDescription>Update roles and permissions for {member?.full_name}</DialogDescription>
        </DialogHeader>
        <div className="space-y-6">
          {/* Member Information */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Member Information</h3>
            </div>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="edit-full-name">Full Name</Label>
                <Input
                  id="edit-full-name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Enter full name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter email address"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-member-since">Member Since</Label>
                <Input
                  id="edit-member-since"
                  type="date"
                  value={memberSince}
                  onChange={(e) => setMemberSince(e.target.value)}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Main Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Main Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Chair and Treasurer cannot be on same member
              </p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(mainRoles).map(([role, value], index) => {
                  // Left column (even indices) show tooltip on right, right column on left
                  const tooltipSide = index % 2 === 0 ? "right" : "left";
                  
                  return (
                    <div
                      key={role}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">
                          {role}
                        </Label>
                         <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent 
                            side={tooltipSide}
                            align="start" 
                            className="max-w-xs z-[9999] bg-popover"
                            sideOffset={8}
                            collisionPadding={40}
                          >
                            <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch
                        id={`edit-${role}`}
                        checked={value}
                        onCheckedChange={(checked) => {
                          // If enabling chair, disable treasurer (and vice versa)
                          if (checked) {
                            if (role === 'chair') {
                              setMainRoles((prev) => ({ ...prev, chair: true, treasurer: false }));
                            } else if (role === 'treasurer') {
                              setMainRoles((prev) => ({ ...prev, treasurer: true, chair: false }));
                            } else {
                              setMainRoles((prev) => ({ ...prev, [role]: checked }));
                            }
                          } else {
                            setMainRoles((prev) => ({ ...prev, [role]: checked }));
                          }
                        }}
                        disabled={hasOccasionalHelper}
                      />
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Roles */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Roles</h3>
              <p className="text-xs text-muted-foreground mb-3">
                Occasional helpers cannot have main roles
              </p>
            </div>
            <TooltipProvider>
              <div className="grid md:grid-cols-2 gap-3">
                {Object.entries(additionalRoles).map(([role, value], index) => {
                  // Left column (even indices) show tooltip on right, right column on left
                  const tooltipSide = index % 2 === 0 ? "right" : "left";
                  
                  return (
                    <div
                      key={role}
                      className="flex items-center justify-between p-3 border border-border rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`edit-${role}`} className="cursor-pointer capitalize">
                          {role.replace("_", " ")}
                        </Label>
                         <Tooltip>
                          <TooltipTrigger asChild>
                            <Info className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
                          </TooltipTrigger>
                          <TooltipContent 
                            side={tooltipSide}
                            align="start" 
                            className="max-w-xs z-[9999] bg-popover"
                            sideOffset={8}
                            collisionPadding={40}
                          >
                            <p className="text-sm">{ROLE_DESCRIPTIONS[role as keyof typeof ROLE_DESCRIPTIONS]}</p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <Switch
                        id={`edit-${role}`}
                        checked={value}
                        onCheckedChange={(checked) =>
                          setAdditionalRoles((prev) => ({ ...prev, [role]: checked }))
                        }
                        disabled={role === "occasional_helper" ? hasMainRole : hasOccasionalHelper}
                      />
                    </div>
                  );
                })}
              </div>
            </TooltipProvider>
          </div>

          <Separator />

          {/* Additional Access */}
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-sm mb-2">Additional Access</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-documents" className="cursor-pointer">
                    Documents
                  </Label>
                  {additionalAccess.documents && (
                    <div className="mt-2 ml-4">
                      <Select
                        value={documentPermission}
                        onValueChange={(v: "read" | "edit") => setDocumentPermission(v)}
                      >
                        <SelectTrigger className="w-32">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="read">Read Only</SelectItem>
                          <SelectItem value="edit">Edit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </div>
                <Switch
                  id="edit-documents"
                  checked={additionalAccess.documents}
                  onCheckedChange={(checked) =>
                    setAdditionalAccess((prev) => ({ ...prev, documents: checked }))
                  }
                  disabled={hasOccasionalHelper}
                />
              </div>
              <div className="flex items-center justify-between p-3 border border-border rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="edit-expenses" className="cursor-pointer">
                    Expense Claims
                  </Label>
                </div>
                <Switch
                  id="edit-expenses"
                  checked={additionalAccess.expenses}
                  onCheckedChange={(checked) =>
                    setAdditionalAccess((prev) => ({ ...prev, expenses: checked }))
                  }
                  disabled={hasOccasionalHelper}
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* DBS Check Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
              <h3 className="font-semibold text-sm">DBS Check</h3>
            </div>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="dbs-checked">DBS Check Required</Label>
                <p className="text-xs text-muted-foreground">Record DBS check details for this member</p>
              </div>
              <Switch
                id="dbs-checked"
                checked={dbsChecked}
                onCheckedChange={setDbsChecked}
              />
            </div>
            
            {dbsChecked && (
              <div className="space-y-3 pl-4 border-l-2 border-primary/20">
                <div className="space-y-2">
                  <Label htmlFor="dbs-type">DBS Type</Label>
                  <Select value={dbsType} onValueChange={setDbsType}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select DBS type" />
                    </SelectTrigger>
                    <SelectContent>
                      {DBS_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dbs-certificate">Certificate Number</Label>
                  <Input
                    id="dbs-certificate"
                    value={dbsCertificateNumber}
                    onChange={(e) => setDbsCertificateNumber(e.target.value)}
                    placeholder="e.g., 001234567890"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="dbs-issue-date">Valid From</Label>
                    <Input
                      id="dbs-issue-date"
                      type="date"
                      value={dbsIssueDate}
                      onChange={(e) => setDbsIssueDate(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="dbs-expiry-date">Expiry Date</Label>
                    <Input
                      id="dbs-expiry-date"
                      type="date"
                      value={dbsExpiryDate}
                      onChange={(e) => setDbsExpiryDate(e.target.value)}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="dbs-notes">Notes</Label>
                  <Textarea
                    id="dbs-notes"
                    value={dbsNotes}
                    onChange={(e) => setDbsNotes(e.target.value)}
                    placeholder="Optional notes about this DBS check"
                    rows={2}
                  />
                </div>
              </div>
            )}
          </div>

          <Separator />

          {/* Forum Access Control */}
          <div className="space-y-3">
            <h3 className="font-semibold text-sm">Forum Access</h3>
            <div className="flex items-center justify-between p-3 border border-border rounded-lg">
              <div className="space-y-0.5">
                <Label htmlFor="forum-access">Disable Forum Access</Label>
                <p className="text-xs text-muted-foreground">Ban this user from accessing the forum</p>
              </div>
              <Switch
                id="forum-access"
                checked={forumAccessDisabled}
                onCheckedChange={setForumAccessDisabled}
              />
            </div>
          </div>

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={loading} className="flex-1">
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
